/**
 * Colorblind-friendly and scientific color palettes for choropleth maps
 * Based on matplotlib/plotly standards for perceptually uniform color schemes
 */

// Scientific multi-color palettes (colorblind-friendly)
// Each palette has 6-8 color stops for smooth gradients

const PALETTES = {
  // Viridis: blue → green → yellow (default, most popular)
  viridis: [
    '#440154', // dark purple
    '#31688e', // blue
    '#35b779', // green
    '#6ece58', // yellow-green
    '#b5de2b', // yellow
    '#fde725'  // bright yellow
  ],

  // Plasma: purple → pink → yellow
  plasma: [
    '#0d0887', // dark purple
    '#6a00a8', // purple
    '#b12a90', // magenta
    '#e16462', // red-pink
    '#fca636', // orange
    '#f0f921'  // yellow
  ],

  // Inferno: black → red → yellow
  inferno: [
    '#000004', // black
    '#3b0f70', // dark purple
    '#8c2981', // purple
    '#de4968', // red-pink
    '#fe9f6d', // orange
    '#fcfdbf'  // yellow-white
  ],

  // Turbo: blue → cyan → green → yellow → red
  turbo: [
    '#30123b', // dark purple
    '#4145ab', // blue
    '#4675ed', // light blue
    '#20a4c1', // cyan
    '#0cf970', // green
    '#7dfe2e', // yellow-green
    '#feba02', // yellow
    '#e4461a', // red
    '#7a0403'  // dark red
  ],

  // Colorblind-friendly sequential palettes (replacing single-color opacity options)
  // These use distinct hues and lightness values that are distinguishable for colorblind users

  // Blue: light blue → dark blue (colorblind-friendly, uses lightness variation)
  blue: [
    '#deebf7', // very light blue
    '#c6dbef', // light blue
    '#9ecae1', // medium-light blue
    '#6baed6', // medium blue
    '#4292c6', // medium-dark blue
    '#2171b5', // dark blue
    '#084594'  // very dark blue
  ],

  // Orange: light yellow → dark orange (colorblind-friendly)
  orange: [
    '#fff7bc', // very light yellow
    '#fee391', // light yellow
    '#fec44f', // medium-light yellow-orange
    '#fe9929', // medium orange
    '#ec7014', // medium-dark orange
    '#cc4c02', // dark orange
    '#8c2d04'  // very dark orange-brown
  ],

  // Green: light green → dark green (colorblind-friendly)
  green: [
    '#e5f5e0', // very light green
    '#c7e9c0', // light green
    '#a1d99b', // medium-light green
    '#74c476', // medium green
    '#41ab5d', // medium-dark green
    '#238b45', // dark green
    '#005a32'  // very dark green
  ],

  // Red: light pink → dark red (colorblind-friendly)
  red: [
    '#fee0d2', // very light pink
    '#fcbba1', // light pink
    '#fc9272', // medium-light pink
    '#fb6a4a', // medium pink-red
    '#ef3b2c', // medium-dark red
    '#cb181d', // dark red
    '#99000d'  // very dark red
  ],

  // Diverging palettes (low → neutral midpoint → high)

  // RdBu: red → white → blue (ColorBrewer)
  rdbu: [
    '#67001f',
    '#b2182b',
    '#d6604d',
    '#f4a582',
    '#fddbc7',
    '#f7f7f7',
    '#d1e5f0',
    '#92c5de',
    '#4393c3',
    '#2166ac',
    '#053061'
  ],

  // BrBG: brown → white → teal (ColorBrewer, colorblind-friendly)
  brbg: [
    '#543005',
    '#8c510a',
    '#bf812d',
    '#dfc27d',
    '#f6e8c3',
    '#f5f5f5',
    '#c7eae5',
    '#80cdc1',
    '#35978f',
    '#01665e',
    '#003c30'
  ],

  // PiYG: magenta → white → green (ColorBrewer)
  piyg: [
    '#8e0152',
    '#c51b7d',
    '#de77ae',
    '#f1b6da',
    '#fde0ef',
    '#f7f7f7',
    '#e6f5d0',
    '#b8e186',
    '#7fbc41',
    '#4d9221',
    '#276419'
  ],

  // Spectral: red → yellow → blue (ColorBrewer)
  spectral: [
    '#9e0142',
    '#d53e4f',
    '#f46d43',
    '#fdae61',
    '#fee08b',
    '#ffffbf',
    '#e6f598',
    '#abdda4',
    '#66c2a5',
    '#3288bd',
    '#5e4fa2'
  ]
};

/**
 * Get array of hex colors for a given palette name
 * @param {string} paletteName - Name of the palette
 * @param {boolean} inverted - Whether to invert the palette (reverse order)
 * @returns {string[]} Array of hex color strings
 */
export const getPaletteColors = (paletteName, inverted = false) => {
  if (!paletteName) return null;

  const palette = PALETTES[paletteName];
  if (!palette) return null;

  // If it's a single color (string), return array with that color
  if (typeof palette === 'string') {
    return [palette];
  }

  // Return array of colors (inverted if requested)
  return inverted ? [...palette].reverse() : palette;
};

/**
 * Map interval index to palette color index
 * @param {number} intervalIndex - Interval index (0 to numIntervals-1)
 * @param {number} numIntervals - Total number of intervals
 * @param {number} paletteLength - Length of palette color array
 * @returns {number} Color index in palette array
 */
export const mapIntervalToColorIndex = (intervalIndex, numIntervals, paletteLength) => {
  if (numIntervals === 1) return 0;
  return Math.min(
    Math.round((intervalIndex / (numIntervals - 1)) * (paletteLength - 1)),
    paletteLength - 1
  );
};

/**
 * Interpolate a value to a color from a palette
 * @param {number} value - The value to map
 * @param {number} min - Minimum value in range
 * @param {number} max - Maximum value in range
 * @param {string} paletteName - Name of the palette to use
 * @returns {string} Hex color string
 */
export const interpolateColor = (value, min, max, paletteName) => {
  const colors = getPaletteColors(paletteName);
  if (!colors || colors.length === 0) return '#10b981'; // fallback

  // Single color palette (backward compatibility)
  if (colors.length === 1) {
    return colors[0];
  }

  // Normalize value to 0-1 range
  const normalized = (value - min) / (max - min);
  const clamped = Math.max(0, Math.min(1, normalized));

  // Map to color index
  const colorIndex = clamped * (colors.length - 1);
  const lowerIndex = Math.floor(colorIndex);
  const upperIndex = Math.min(Math.ceil(colorIndex), colors.length - 1);
  const t = colorIndex - lowerIndex;

  // If exactly on a color stop, return that color
  if (t < 0.001) {
    return colors[lowerIndex];
  }
  if (t > 0.999) {
    return colors[upperIndex];
  }

  // Interpolate between two colors
  return interpolateHexColor(colors[lowerIndex], colors[upperIndex], t);
};

/**
 * Interpolate between two hex colors
 * @param {string} color1 - First hex color
 * @param {string} color2 - Second hex color
 * @param {number} t - Interpolation factor (0-1)
 * @returns {string} Interpolated hex color
 */
const interpolateHexColor = (color1, color2, t) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * t);
  const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * t);
  const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * t);

  return rgbToHex(r, g, b);
};

/**
 * Convert hex color to RGB
 * @param {string} hex - Hex color string
 * @returns {{r: number, g: number, b: number}} RGB object
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 16, g: 185, b: 129 }; // fallback to emerald
};

/**
 * Convert RGB to hex color
 * @param {number} r - Red component (0-255)
 * @param {number} g - Green component (0-255)
 * @param {number} b - Blue component (0-255)
 * @returns {string} Hex color string
 */
const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

/**
 * Get CSS gradient string for palette preview
 * @param {string} paletteName - Name of the palette
 * @param {boolean} inverted - Whether to invert the palette (reverse order)
 * @returns {string} CSS linear-gradient string
 */
export const getPalettePreview = (paletteName, inverted = false) => {
  const colors = getPaletteColors(paletteName, inverted);
  if (!colors || colors.length === 0) return 'linear-gradient(to right, #10b981, #10b981)';

  if (colors.length === 1) {
    return `linear-gradient(to right, ${colors[0]}, ${colors[0]})`;
  }

  const colorStops = colors.map((color, index) => {
    const percent = (index / (colors.length - 1)) * 100;
    return `${color} ${percent}%`;
  }).join(', ');

  return `linear-gradient(to right, ${colorStops})`;
};

/**
 * Get all available palette names
 * @returns {string[]} Array of palette names
 */
export const getAvailablePalettes = () => {
  return Object.keys(PALETTES);
};

/**
 * Get scientific palette names only
 * @returns {string[]} Array of scientific palette names
 */
export const getScientificPalettes = () => {
  return ['viridis', 'plasma', 'inferno', 'turbo'];
};

/**
 * Get colorblind-friendly sequential palette names (replacing single-color options)
 * @returns {string[]} Array of colorblind-friendly sequential palette names
 */
export const getSingleColorPalettes = () => {
  return ['blue', 'orange', 'green', 'red'];
};

/**
 * Get diverging palette names (low → midpoint → high)
 * @returns {string[]} Array of diverging palette names
 */
export const getDivergingPalettes = () => {
  return ['rdbu', 'brbg', 'piyg', 'spectral'];
};

/**
 * Check if a palette name is valid
 * @param {string} paletteName - Name to check
 * @returns {boolean} True if valid
 */
export const isValidPalette = (paletteName) => {
  return paletteName && PALETTES.hasOwnProperty(paletteName);
};

/**
 * Get default palette name
 * @returns {string} Default palette name (viridis)
 */
export const getDefaultPalette = () => {
  return 'viridis';
};
